import type { Product } from '../../../@types/Product.js';

export type WagerResponse = {
  accountBalance: number;
  wagerAmounts: {
    totalAmount: string;
    betAmount: string;
  };
  wagerClientSerial: string;
};

export type ErrorResponse = {
  error: {
    code: string;
    message?: string;
  };
};

export type MarketplaceWagerApiResponse = {
  CreateWagerReply: {
    betAmount: string;
    serial: string;
  };
};

export type MarketplaceWagerResponse = Omit<WagerResponse, 'accountBalance'>;

export type DrawResponse = {
  draw: {
    regOpenTime: string;
    fund?: {
      rolloverIn: string;
      extraMoney: string;
    };
  };
};

export type JackpotResponse = {
  jackpot: {
    jackpots: [
      {
        jackpotAmount: string;
      },
    ];
    guaranteedJackpots: [
      {
        jackpotAmount: string;
      },
    ];
  };
};

export type MultifetchResponse<TObject> = {
  responses: (TObject | ErrorResponse)[];
};

export type DrawId = `${Product}_${number}`;

export type ProductToTrack = {
  product: Product;
  drawNumber: number;
};

function isObject(data: unknown): data is object {
  return typeof data === 'object' && data !== null;
}

/**
 * Function to check if the data is a WagerResponse.
 *
 * @param data The data to check
 * @returns True if the data is a WagerResponse
 */
export function isWagerResponse(data: unknown): data is WagerResponse {
  if (!isObject(data)) {
    return false;
  }
  if (!('accountBalance' in data)) {
    return false;
  }
  if (!('wagerAmounts' in data)) {
    return false;
  }
  if (!isObject(data.wagerAmounts)) {
    return false;
  }
  if (!('totalAmount' in data.wagerAmounts)) {
    return false;
  }

  return typeof data.accountBalance === 'number' && typeof data.wagerAmounts.totalAmount === 'string';
}

/**
 * Function to check if the data is an ErrorResponse.
 *
 * @param data The data to check
 * @returns True if the data is an ErrorResponse
 */
export function isErrorResponse(data: unknown): data is ErrorResponse {
  if (!isObject(data)) {
    return false;
  }
  if (!('error' in data)) {
    return false;
  }
  if (!isObject(data.error)) {
    return false;
  }
  if (!('code' in data.error)) {
    return false;
  }

  // Additional check for specific error code 41 which is used for marketplace wager already paid
  return typeof data.error.code === 'string' || data.error.code === 41;
}

/**
 * Function to check if the data is a DrawResponse.
 *
 * @param data The data to check
 * @returns True if the data is a DrawResponse
 */
export function isDrawResponse(data: unknown): data is DrawResponse {
  if (!isObject(data)) {
    return false;
  }
  if (!('draw' in data) || !isObject(data.draw)) {
    return false;
  }
  if (!('fund' in data.draw) || !isObject(data.draw.fund)) {
    return false;
  }
  if (!('rolloverIn' in data.draw.fund) || !('extraMoney' in data.draw.fund)) {
    return false;
  }
  if (typeof data.draw.fund.rolloverIn !== 'string' || typeof data.draw.fund.extraMoney !== 'string') {
    return false;
  }

  return true;
}

/**
 * Function to check if the data is a JackpotResponse.
 *
 * @param data The data to check
 * @returns True if the data is a JackpotResponse
 */
export function isJackpotResponse(data: unknown): data is JackpotResponse {
  if (!isObject(data)) {
    return false;
  }
  if (!('jackpot' in data) || !isObject(data.jackpot)) {
    return false;
  }
  if (!('jackpots' in data.jackpot) || !('guaranteedJackpots' in data.jackpot)) {
    return false;
  }
  if (!Array.isArray(data.jackpot.jackpots) || !Array.isArray(data.jackpot.guaranteedJackpots)) {
    return false;
  }

  return (
    data.jackpot.jackpots.every((jackpot) => isObject(jackpot) && 'jackpotAmount' in jackpot) &&
    data.jackpot.guaranteedJackpots.every((jackpot) => isObject(jackpot) && 'jackpotAmount' in jackpot)
  );
}
